import React, { useState, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { clsx } from 'clsx'
import {
  LayoutDashboard,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  LifeBuoy,
  Mail,
  ChevronDown,
  Building2,
  FileText,
  Megaphone,
  Radar,
  Tv,
  Sparkles,
  X
} from 'lucide-react'
import CustomerSelectorModal from './CustomerSelectorModal'
import Tooltip from '@/components/ui/Tooltip'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'
import { useAuthStore } from '@/stores/authStore'
import { User } from '@/services/authService'
import {
  canAccessSupportPage,
  hasPermission,
  hasAnyPermission,
  canManageOrganization
} from '@/utils/permissions'
import { useNavigationTracking } from '@/contexts/AnalyticsContext'
import { isSponsorshipsEnabled, isBrandScoutEnabled, isNewsletterEnabled, isInstagramGraphApiEnabled, isBroadcastAnalysisEnabled } from '@/utils/featureFlags'
import usePermissionsStore from '@/stores/permissionsStore'

const ENHANCED_API_HINT_KEY = 'enhanced_api_connect_hint_seen'

interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
}

// Menu item type with flexible permission options
type MenuItem = {
  icon: any
  label: string
  path: string
  view: string
  shouldDisable?: (selectedCustomer: any) => boolean // Optional function to determine if menu item should be disabled
  getTooltipMessage?: (selectedCustomer: any) => string // Optional function to get tooltip message when disabled
} & (
  | { requiresRoleCheck: true; checkAccess: (user: User | null) => boolean } // Custom role check function
  | { requiredPermissions: string[] } // Requires any of these permissions
  | { requiredPermission: string } // Requires specific permission
  | {}
) // No restrictions

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggle }) => {
  const { selectedCustomer, selectedAccount, customerAccounts, loadCustomers } =
    useGlobalCustomerStore()
  const { user, organizationLogoUrl } = useAuthStore()
  const { loadPermissions, hasFeature } = usePermissionsStore()
  const [showCustomerSelector, setShowCustomerSelector] = useState(false)
  // One-time onboarding hint on the Settings nav item (Enhanced API connection).
  const [showConnectHint, setShowConnectHint] = useState(false)
  const { trackSidebarNavigation } = useNavigationTracking()
  const location = useLocation()
  const navigate = useNavigate()

  // Gate the hint to orgs that can use the Enhanced API AND users who can reach
  // Settings → Social Accounts (where the connection lives).
  const enhancedApiAvailable =
    isInstagramGraphApiEnabled() && hasFeature('instagram_graph_api') && canManageOrganization(user)

  useEffect(() => {
    if (user?.organizationId) {
      loadPermissions(user.organizationId)
    }
  }, [user?.organizationId, loadPermissions])

  // Show the Enhanced API connect hint once per user.
  useEffect(() => {
    if (!localStorage.getItem(ENHANCED_API_HINT_KEY)) {
      setShowConnectHint(true)
    }
  }, [])

  const dismissConnectHint = () => {
    setShowConnectHint(false)
    localStorage.setItem(ENHANCED_API_HINT_KEY, 'true')
  }

  // Handle navigation with tracking
  const handleNavigation = (path: string) => {
    trackSidebarNavigation(location.pathname, path)
    navigate(path)
  }

  // Load customers on mount
  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  // Auto-navigate to appropriate dashboard when customer changes
  useEffect(() => {
    if (!selectedCustomer) return

    // Special case: if totalPlatformAccounts = 1 and totalPublications = 1,
    // it means the 1 account is a newsletter publication (not a social media account)
    const isNewsletterOnly =
      selectedCustomer.totalPlatformAccounts === 1 &&
      selectedCustomer.totalPublications === 1

    const hasSocialAccounts = isNewsletterOnly
      ? false
      : selectedCustomer.totalPlatformAccounts > 0
    const hasNewsletterAccounts = selectedCustomer.totalPublications > 0

    // Check if current view is disabled for this customer
    const currentViewIsDisabled =
      (location.pathname === '/social-media' && !hasSocialAccounts) ||
      (location.pathname === '/newsletter' && !hasNewsletterAccounts) ||
      (location.pathname === '/reports' && !selectedCustomer)

    // Only auto-navigate if the current view is disabled
    if (!currentViewIsDisabled) return

    // Determine which dashboard to navigate to
    if (hasSocialAccounts && hasNewsletterAccounts) {
      // Both available - navigate to social-media
      navigate('/social-media')
    } else if (hasSocialAccounts) {
      // Only social accounts - navigate to social-media
      navigate('/social-media')
    } else if (hasNewsletterAccounts) {
      // Only newsletter accounts - navigate to newsletter
      navigate('/newsletter')
    }
  }, [selectedCustomer, location.pathname, navigate])

  // Main navigation items with permission requirements
  const mainMenuItems: MenuItem[] = [
    {
      icon: LayoutDashboard,
      label: 'Social Dashboard',
      path: '/social-media',
      view: 'social-media',
      shouldDisable: (selectedCustomer) => {
        if (!selectedCustomer) return true
        // Special case: if totalPlatformAccounts = 1 and totalPublications = 1,
        // the account is a newsletter, not social media
        if (
          selectedCustomer.totalPlatformAccounts === 1 &&
          selectedCustomer.totalPublications === 1
        ) {
          return true
        }
        return selectedCustomer.totalPlatformAccounts === 0
      },
      getTooltipMessage: (selectedCustomer) => {
        if (!selectedCustomer) return 'Please select a customer'
        if (
          selectedCustomer.totalPlatformAccounts === 0 ||
          (selectedCustomer.totalPlatformAccounts === 1 &&
            selectedCustomer.totalPublications === 1)
        ) {
          return 'No social media accounts available for this customer'
        }
        return ''
      }
    },
    {
      icon: Tv,
      label: 'Broadcast Dashboard',
      path: '/broadcasts',
      view: 'broadcasts',
      requiresRoleCheck: true,
      checkAccess: () => isBroadcastAnalysisEnabled(),
      // Two layers, two jobs: the PostHog flag above decides whether this is
      // drawn at all, the organisation feature decides whether it can be
      // clicked. Checking only the first is why the entry looked available
      // and then the page refused.
      shouldDisable: (selectedCustomer) =>
        !selectedCustomer || !hasFeature('broadcast_analysis'),
      getTooltipMessage: (selectedCustomer) => {
        if (!selectedCustomer) return 'Please select a customer'
        if (!hasFeature('broadcast_analysis'))
          return 'Broadcast analysis is not enabled for this organization'
        return ''
      }
    },
    {
      icon: Mail,
      label: 'Newsletter Dashboard',
      path: '/newsletter',
      view: 'newsletter',
      requiresRoleCheck: true,
      checkAccess: () => isNewsletterEnabled(),
      shouldDisable: (selectedCustomer) =>
        !selectedCustomer || selectedCustomer.totalPublications === 0,
      getTooltipMessage: (selectedCustomer) => {
        if (!selectedCustomer) return 'Please select a customer'
        if (selectedCustomer.totalPublications === 0)
          return 'No publications available for this customer'
        return ''
      }
    },
    {
      icon: Megaphone,
      label: 'Sponsorships',
      path: '/sponsorships',
      view: 'sponsorships',
      requiresRoleCheck: true,
      checkAccess: () => isSponsorshipsEnabled(),
      shouldDisable: (selectedCustomer) => !selectedCustomer,
      getTooltipMessage: (selectedCustomer) => {
        if (!selectedCustomer) return 'Please select a customer'
        return ''
      }
    },
    {
      icon: Radar,
      label: 'Brand Scout',
      path: '/brand-scout',
      view: 'brand-scout',
      requiresRoleCheck: true,
      checkAccess: () => isBrandScoutEnabled(),
      shouldDisable: (selectedCustomer) => !selectedCustomer,
      getTooltipMessage: (selectedCustomer) => {
        if (!selectedCustomer) return 'Please select a customer'
        return ''
      }
    },
    {
      icon: FileText,
      label: 'Reports',
      path: '/reports',
      view: 'reports',
      shouldDisable: (selectedCustomer) => !selectedCustomer,
      getTooltipMessage: (selectedCustomer) => {
        if (!selectedCustomer) return 'Please select a customer'
        return ''
      }
    },
    // TODO: Future implementation - Charts functionality
    // { icon: BarChart3, label: 'Charts', path: '/charts', view: 'charts' },
    // TODO: Future implementation - Upload Data functionality
    // { icon: Upload, label: 'Upload Data', path: '/upload-data', view: 'upload-data' },
    // TODO: Future implementation - Data Sources functionality
    // { icon: Database, label: 'Data Sources', path: '/data-sources', view: 'data-sources' },
    {
      icon: LifeBuoy,
      label: 'Support',
      path: '/support',
      view: 'support',
      requiresRoleCheck: true, // Use custom role check
      checkAccess: canAccessSupportPage // Only System Admin and Support roles
    },
    { icon: HelpCircle, label: 'Help', path: '/help', view: 'help' }
  ]

  // Bottom settings items
  const bottomMenuItems: MenuItem[] = [
    { icon: Settings, label: 'Settings', path: '/settings', view: 'settings' }
  ]

  // Filter menu items based on user permissions
  const filteredMainMenuItems = mainMenuItems.filter((item) => {
    // Check if item requires custom role/permission check
    if (
      'requiresRoleCheck' in item &&
      item.requiresRoleCheck &&
      'checkAccess' in item
    ) {
      return item.checkAccess(user)
    }

    // Check if item requires specific permissions
    if ('requiredPermissions' in item && item.requiredPermissions) {
      return hasAnyPermission(user, item.requiredPermissions)
    }

    // Check if item requires a specific permission
    if ('requiredPermission' in item && item.requiredPermission) {
      return hasPermission(user, item.requiredPermission)
    }

    // No restrictions, show to everyone
    return true
  })

  const filteredBottomMenuItems = bottomMenuItems.filter((item) => {
    // Check if item requires custom role/permission check
    if (
      'requiresRoleCheck' in item &&
      item.requiresRoleCheck &&
      'checkAccess' in item
    ) {
      return item.checkAccess(user)
    }

    // Check if item requires specific permissions
    if ('requiredPermissions' in item && item.requiredPermissions) {
      return hasAnyPermission(user, item.requiredPermissions)
    }

    // Check if item requires a specific permission
    if ('requiredPermission' in item && item.requiredPermission) {
      return hasPermission(user, item.requiredPermission)
    }

    // No restrictions, show to everyone
    return true
  })

  return (
    <motion.aside
      initial={{ x: -100 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        'bg-slate-800 shadow-xl transition-all duration-300 h-full flex flex-col',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo and Toggle */}
      <div className='flex items-center justify-between p-4'>
        {!collapsed && (
          <div className='flex items-center space-x-2'>
            {organizationLogoUrl ? (
              <img
                src={organizationLogoUrl}
                alt={user?.organizationName || 'Organization'}
                className='w-8 h-8 object-contain rounded'
                onError={(e) => {
                  // Fallback to AVI logo on error
                  e.currentTarget.src = '/favicon.png'
                  e.currentTarget.alt = 'AVI'
                }}
              />
            ) : (
              <img src='/favicon.png' alt='AVI' className='w-8 h-8' />
            )}
            <h1
              className={`${
                organizationLogoUrl ? 'text-[1rem]' : 'text-2xl'
              } font-bold text-white`}
            >
              {organizationLogoUrl ? user?.organizationName || 'AVI' : 'AVI'}
            </h1>
          </div>
        )}

        {onToggle && (
          <button
            onClick={onToggle}
            className='p-1 rounded-lg hover:bg-slate-700 transition-colors'
          >
            {collapsed ? (
              <ChevronRight className='h-5 w-5 text-slate-400' />
            ) : (
              <ChevronLeft className='h-5 w-5 text-slate-400' />
            )}
          </button>
        )}
      </div>

      {/* Customer Selector */}
      {!collapsed && (
        <div className='px-4 pb-4 pt-4'>
          <button
            onClick={() => setShowCustomerSelector(true)}
            className='w-full flex items-center justify-between p-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors group'
          >
            <div className='flex items-center space-x-2.5 min-w-0'>
              <div className='w-7 h-7 bg-slate-600 group-hover:bg-slate-500 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Building2 className='h-3.5 w-3.5 text-slate-300' />
              </div>
              <div className='text-left min-w-0'>
                <p className='text-xs font-medium text-white truncate'>
                  {selectedCustomer
                    ? selectedCustomer.customerName
                    : 'Select Customer'}
                </p>
                {selectedCustomer && (
                  <p className='text-[10px] text-slate-400 truncate'>
                    {selectedAccount?.type === 'individual' && selectedAccount.account
                      ? `@${selectedAccount.account.username} • ${selectedAccount.account.platformDisplayName}`
                      : `${customerAccounts[selectedCustomer.id]?.length ?? selectedCustomer.totalPlatformAccounts} accounts`}
                  </p>
                )}
              </div>
            </div>
            <ChevronDown className='h-3.5 w-3.5 text-slate-400 flex-shrink-0' />
          </button>
        </div>
      )}

      <div className='flex flex-col flex-1'>
        {/* Main Navigation */}
        <nav className='mt-8 flex-1'>
          <ul className='space-y-1 px-2'>
            {filteredMainMenuItems.map((item) => {
              const Icon = item.icon
              const isDisabled = item.shouldDisable?.(selectedCustomer) ?? false
              const tooltipMessage =
                item.getTooltipMessage?.(selectedCustomer) ?? ''

              const menuButton = isDisabled ? (
                <button
                  disabled={true}
                  className={clsx(
                    'w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    'text-slate-500 cursor-not-allowed opacity-50'
                  )}
                >
                  <Icon className={clsx('h-5 w-5', collapsed ? '' : 'mr-3')} />
                  {!collapsed && <span>{item.label}</span>}
                </button>
              ) : (
                <NavLink
                  to={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={({ isActive }) =>
                    clsx(
                      'w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                      isActive
                        ? 'bg-slate-700 text-white shadow-md'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    )
                  }
                >
                  <Icon className={clsx('h-5 w-5', collapsed ? '' : 'mr-3')} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              )

              return (
                <li key={item.path}>
                  {isDisabled && !collapsed && tooltipMessage ? (
                    <Tooltip content={tooltipMessage} placement='right'>
                      {menuButton}
                    </Tooltip>
                  ) : (
                    menuButton
                  )}
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Bottom Settings */}
        <nav className='pb-4'>
          <ul className='space-y-1 px-2'>
            {filteredBottomMenuItems.map((item) => {
              const Icon = item.icon
              const hintActive =
                item.view === 'settings' && showConnectHint && enhancedApiAvailable
              return (
                <li key={item.path} className='relative'>
                  <NavLink
                    to={item.path}
                    onClick={() => {
                      if (hintActive) dismissConnectHint()
                      handleNavigation(item.path)
                    }}
                    className={({ isActive }) =>
                      clsx(
                        'w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                        isActive
                          ? 'bg-slate-700 text-white shadow-md'
                          : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                        hintActive && 'ring-2 ring-blue-400'
                      )
                    }
                  >
                    <Icon
                      className={clsx('h-5 w-5', collapsed ? '' : 'mr-3')}
                    />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>

                  {/* One-time Enhanced API connect hint, anchored to Settings */}
                  {hintActive && (
                    <div className='absolute left-full bottom-0 ml-3 z-50 w-64'>
                      {/* arrow pointing left toward the nav item */}
                      <div className='absolute -left-1 bottom-3 h-2.5 w-2.5 rotate-45 bg-gray-900' />
                      <div className='relative bg-gray-900 text-white rounded-lg shadow-lg p-3'>
                        <div className='flex items-start justify-between gap-2'>
                          <div className='flex items-start gap-2'>
                            <Sparkles className='h-4 w-4 mt-0.5 flex-shrink-0 text-blue-300' />
                            <p className='text-xs leading-relaxed'>
                              <span className='font-semibold'>New!</span> Connect the Facebook Business account that owns your Instagram to unlock <span className='font-semibold'>views, shares &amp; saves</span>, <span className='font-semibold'>audience demographics</span>, and <span className='font-semibold'>Stories analytics</span> — data scraping can't capture. Find it under <span className='font-semibold'>Settings → Social Accounts</span>.
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissConnectHint() }}
                            className='text-gray-400 hover:text-white flex-shrink-0'
                            aria-label='Dismiss'
                          >
                            <X className='h-3.5 w-3.5' />
                          </button>
                        </div>
                        <div className='mt-2 flex justify-end'>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/settings?tab=accounts'); dismissConnectHint() }}
                            className='text-[11px] font-medium text-blue-300 hover:text-blue-200'
                          >
                            Take me there
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>
      </div>
      {/* Customer Selector Modal */}
      <CustomerSelectorModal
        isOpen={showCustomerSelector}
        onClose={() => setShowCustomerSelector(false)}
      />
    </motion.aside>
  )
}

export default Sidebar
